import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, useNavigate, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import {
  FieldLabel,
  FieldLabelRow,
  GoogleMark,
  Headline,
  MagicLinkCallout,
  OrDivider,
  PageEyebrow,
  SplitShell,
  TermsLine,
} from '../components/auth-shells.js';
import { PasswordInput } from '../components/password-input.js';
import { magToast } from '../components/toaster.js';
import { signIn } from '../lib/auth-client.js';
import { founderErrorCopy } from '../lib/error-copy.js';

// ?next=<encoded-relative-path> — used by the invite accept flow so the
// user lands back on /accept?token=… after signing in. We only accept
// same-origin paths (must start with /) to defeat open-redirect.
// ?email=<address> — pre-fills the email input (invite flow). Stays
// editable so a typo on the invite is recoverable; the accept endpoint
// is the real check.
const searchSchema = z.object({
  next: z.string().optional(),
  email: z.string().optional(),
});

function safeNext(next: string | undefined): string {
  if (!next) return '/home';
  try {
    const decoded = decodeURIComponent(next);
    if (!decoded.startsWith('/')) return '/home';
    return decoded;
  } catch {
    return '/home';
  }
}

export const Route = createFileRoute('/login')({
  validateSearch: searchSchema,
  component: LoginPage,
});

// Handoff §15.2.1 — split shell. Google on top, email/password in the
// middle, magic-link callout below. Idle Mag in the brand panel.
function LoginPage() {
  const { t } = useLingui();

  const router = useRouter();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const nextPath = safeNext(search.next);
  const callbackURL = `${window.location.origin}${nextPath}`;

  const [email, setEmail] = useState(search.email?.trim() ?? '');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState<'google' | 'password' | 'magic' | null>(null);
  // Inline error banner — toasts auto-dismiss in ~4s and disappear if
  // the founder is reading the form. an inline message persists until
  // they try again. error fmt: { title, hint?, retry?: () => void }.
  const [errorBanner, setErrorBanner] = useState<{
    title: string;
    hint?: string;
  } | null>(null);

  const clearError = () => setErrorBanner(null);

  const handleGoogle = async () => {
    clearError();
    setPending('google');
    let result: Awaited<ReturnType<typeof signIn.social>>;
    try {
      result = await signIn.social({
        provider: 'google',
        callbackURL,
      });
    } catch (err) {
      const message = founderErrorCopy(err, 'try again.');
      setErrorBanner({
        title: "google didn't open the door.",
        hint: message,
      });
      magToast.error(i18n._(msg`google didn't open the door.`), message);
      setPending(null);
      return;
    }
    if (result.error) {
      const message = result.error.message ?? 'try again.';
      setErrorBanner({ title: "google didn't open the door.", hint: message });
      magToast.error(i18n._(msg`google didn't open the door.`), message);
      setPending(null);
      return;
    }
    // better-auth returns { data: { url, redirect: true } } for social
    // sign-in. it's the dashboard's job to navigate to the oauth url.
    // earlier code jumped straight to nextPath without going through
    // google, which is why /api/me returned 401 — the user never
    // actually authenticated.
    const redirectUrl = (result.data as { url?: string; redirect?: boolean } | undefined)?.url;
    if (redirectUrl) {
      window.location.href = redirectUrl;
      return;
    }
    // No redirect URL means better-auth auto-handled the navigation
    // (older API shape). Fall through to nextPath.
    await router.invalidate();
    window.location.href = nextPath;
  };

  const handlePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setPending('password');
    let result: Awaited<ReturnType<typeof signIn.email>>;
    try {
      result = await signIn.email({
        email,
        password,
        callbackURL,
      });
    } catch (err) {
      const message = founderErrorCopy(err, 'try again.');
      setErrorBanner({ title: "couldn't sign you in.", hint: message });
      magToast.error(i18n._(msg`couldn't sign you in.`), message);
      setPending(null);
      return;
    }
    if (result.error) {
      const message = result.error.message ?? 'check your email and password.';
      setErrorBanner({ title: "that didn't match.", hint: message });
      magToast.error(i18n._(msg`that didn't match.`), message);
      setPending(null);
      return;
    }
    await router.invalidate();
    window.location.href = nextPath;
  };

  const handleMagic = async () => {
    if (!email) {
      setErrorBanner({
        title: 'enter your email first.',
        hint: "i'll send the magic link there.",
      });
      magToast.warning(
        i18n._(msg`enter your email above first.`),
        i18n._(msg`i'll send the link there.`),
      );
      return;
    }
    clearError();
    setPending('magic');
    let result: Awaited<ReturnType<typeof signIn.magicLink>>;
    try {
      result = await signIn.magicLink({
        email,
        callbackURL,
      });
    } catch (err) {
      const message = founderErrorCopy(err, 'try again in a sec.');
      setErrorBanner({ title: "couldn't send the link.", hint: message });
      magToast.error(i18n._(msg`couldn't send the link.`), message);
      setPending(null);
      return;
    }
    if (result.error) {
      const message = result.error.message ?? 'try again in a sec.';
      setErrorBanner({ title: "couldn't send the link.", hint: message });
      magToast.error(i18n._(msg`couldn't send the link.`), message);
      setPending(null);
      return;
    }
    setPending(null);
    navigate({ to: '/magic-link/sent', search: { email } });
  };

  return (
    <SplitShell quote={t`welcome back. i've got a few things to show you.`}>
      <div className="mag-stagger-in" style={{ animationDelay: '320ms' }}>
        <PageEyebrow>
          <Trans>log in</Trans>
        </PageEyebrow>
      </div>
      <div
        className="mag-stagger-in mt-3 flex flex-col gap-1.5"
        style={{ animationDelay: '400ms' }}
      >
        <Headline>
          <Trans>good to see you.</Trans>
        </Headline>
        <p className="text-[15px] text-[var(--ink)]/70">
          <Trans>
            new here?{' '}
            <Link to="/signup" className="link-plum">
              create an account →
            </Link>
          </Trans>
        </p>
      </div>

      <div className="mt-7 flex flex-col gap-4">
        {errorBanner ? (
          <div
            role="alert"
            className="rounded-xl border-[1.5px] border-[var(--pulse)] bg-[var(--pulse)]/8 px-4 py-3 shadow-[3px_3px_0_var(--pulse)]"
          >
            <p className="font-display text-[14px] font-semibold leading-tight text-[var(--ink)]">
              {errorBanner.title}
            </p>
            {errorBanner.hint ? (
              <p className="mt-1 text-[12.5px] leading-snug text-[var(--ink)]/70">
                {errorBanner.hint}
              </p>
            ) : null}
          </div>
        ) : null}
        <button
          type="button"
          onClick={handleGoogle}
          disabled={pending !== null}
          className="mag-stagger-in btn btn-ghost w-full disabled:opacity-60"
          style={{ animationDelay: '480ms' }}
        >
          <GoogleMark />
          {pending === 'google' ? <Trans>redirecting…</Trans> : <Trans>continue with Google</Trans>}
        </button>

        <div className="mag-stagger-in" style={{ animationDelay: '560ms' }}>
          <OrDivider />
        </div>

        <form
          onSubmit={handlePassword}
          className="mag-stagger-in flex flex-col gap-3"
          style={{ animationDelay: '640ms' }}
        >
          <label className="field">
            <FieldLabel>
              <Trans>email</Trans>
            </FieldLabel>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t`founder@startup.com`}
            />
          </label>

          {/* biome-ignore lint/a11y/noLabelWithoutControl: <PasswordInput> renders a real <input> — biome can't see through the component boundary. */}
          <label className="field">
            <FieldLabelRow>
              <FieldLabel>
                <Trans>password</Trans>
              </FieldLabel>
              <Link to="/forgot-password" search={{ email }} className="link-quiet">
                <Trans>forgot?</Trans>
              </Link>
            </FieldLabelRow>
            <PasswordInput
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
              required
              placeholder="•••••••••"
            />
          </label>

          <button
            type="submit"
            disabled={pending !== null}
            className="btn mt-2 w-full disabled:opacity-60"
          >
            {pending === 'password' ? <Trans>signing in…</Trans> : <Trans>log in</Trans>}
            <span className="btn-arrow">→</span>
          </button>
        </form>

        <div className="mag-stagger-in" style={{ animationDelay: '720ms' }}>
          <MagicLinkCallout
            action={
              <button
                type="button"
                onClick={handleMagic}
                disabled={pending !== null}
                className="link-plum"
              >
                {pending === 'magic' ? <Trans>sending…</Trans> : <Trans>send link →</Trans>}
              </button>
            }
          >
            <p className="font-semibold">
              <Trans>email me a magic link instead.</Trans>
            </p>
            <p className="text-[var(--ink)]/60">
              <Trans>no password to remember.</Trans>
            </p>
          </MagicLinkCallout>
        </div>

        <div className="mag-stagger-in" style={{ animationDelay: '800ms' }}>
          <TermsLine />
        </div>
      </div>
    </SplitShell>
  );
}
